import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Mail, Paperclip, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const meubelTypes = [
  "Houten kist met deksel",
  "Kleine meubels / bijzettafel",
  "Interieurontwerp / Stylist project",
  "Ander idee op maat",
];

const beloften = [
  "Vrijblijvende offerte en advies over hout en constructie",
  "Geen verplichtingen, direct contact met de meubelmaker",
  "Werkplaats gevestigd in Rijswijk",
];

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-[#231C17] border border-[#4F4137] text-white placeholder-[#7A6C60] focus:outline-none focus:border-[#E6AF68] text-sm transition-colors";

const initialForm = {
  naam: "",
  email: "",
  telefoon: "",
  type_meubel: meubelTypes[0],
  omschrijving: "",
};

export const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [schets, setSchets] = useState(null);
  const [versturen, setVersturen] = useState(false);
  const schetsRef = useRef(null);

  const update = (veld) => (e) => setForm({ ...form, [veld]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setVersturen(true);
    try {
      let schets_path = null;
      let schets_naam = null;
      if (schets) {
        const fd = new FormData();
        fd.append("file", schets);
        const { data } = await axios.post(`${API}/uploads/schets`, fd);
        schets_path = data.path;
        schets_naam = data.filename;
      }
      await axios.post(`${API}/aanvragen`, {
        naam: form.naam,
        email: form.email,
        telefoon: form.telefoon || null,
        type_meubel: form.type_meubel,
        omschrijving: form.omschrijving || null,
        schets_path,
        schets_naam,
      });
      toast.success("Aanvraag verzonden!", {
        description: "René neemt zo snel mogelijk persoonlijk contact met je op.",
      });
      setForm(initialForm);
      setSchets(null);
    } catch (err) {
      toast.error("Verzenden mislukt", {
        description: err.response?.data?.detail || "Probeer het opnieuw of neem later contact op.",
      });
    } finally {
      setVersturen(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 px-6 bg-[#2B231D] text-[#FBF9F5]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 hr-reveal">
          <span className="text-xs uppercase tracking-widest text-[#E6AF68] font-bold">
            Aanvraag & Contact
          </span>
          <h2 className="text-3xl sm:text-4xl font-normal font-display text-white mt-2 mb-4">
            Laat je meubelidee tot leven komen
          </h2>
          <p className="text-sm text-[#D8CFC4] leading-relaxed mb-8">
            Vul het aanvraagformulier in met jouw wensen. René neemt zo snel mogelijk
            persoonlijk contact met je op om de mogelijkheden te bespreken.
          </p>

          <div className="space-y-4 text-xs text-[#D8CFC4] border-t border-[#43362C] pt-6">
            {beloften.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <Check size={14} className="text-[#E6AF68] shrink-0" />
                <span>{b}</span>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <Mail size={14} className="text-[#E6AF68] shrink-0" />
              <a href="mailto:info@dehoutregisseur.nl" data-testid="contact-email-link" className="hover:text-white transition-colors">
                info@dehoutregisseur.nl
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 hr-reveal">
          <form
            onSubmit={handleSubmit}
            data-testid="aanvraag-form"
            className="bg-[#352B24] p-8 sm:p-10 rounded-3xl border border-[#483B32] shadow-2xl space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="naam" className="block text-xs font-medium text-[#D8CFC4] mb-2">
                  Jouw Naam *
                </label>
                <input
                  type="text"
                  id="naam"
                  data-testid="aanvraag-naam-input"
                  value={form.naam}
                  onChange={update("naam")}
                  className={inputCls}
                  placeholder="bijv. Sanne de Vries"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-[#D8CFC4] mb-2">
                  E-mailadres *
                </label>
                <input
                  type="email"
                  id="email"
                  data-testid="aanvraag-email-input"
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                  placeholder="jouw@email.nl"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="telefoon" className="block text-xs font-medium text-[#D8CFC4] mb-2">
                  Telefoonnummer
                </label>
                <input
                  type="tel"
                  id="telefoon"
                  data-testid="aanvraag-telefoon-input"
                  value={form.telefoon}
                  onChange={update("telefoon")}
                  className={inputCls}
                  placeholder="06 - ..."
                />
              </div>
              <div>
                <label htmlFor="type-meubel" className="block text-xs font-medium text-[#D8CFC4] mb-2">
                  Soort Meubel / Project
                </label>
                <select
                  id="type-meubel"
                  data-testid="aanvraag-type-select"
                  value={form.type_meubel}
                  onChange={update("type_meubel")}
                  className={inputCls}
                >
                  {meubelTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="omschrijving" className="block text-xs font-medium text-[#D8CFC4] mb-2">
                Beschrijf je idee of gewenste afmetingen
              </label>
              <textarea
                id="omschrijving"
                rows="4"
                data-testid="aanvraag-omschrijving-textarea"
                value={form.omschrijving}
                onChange={update("omschrijving")}
                className={inputCls}
                placeholder="Vertel over de gewenste maten, houtsoort of sfeer..."
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[#D8CFC4] mb-2">
                Schets of foto meesturen (optioneel)
              </label>
              <input
                ref={schetsRef}
                type="file"
                accept="image/*"
                data-testid="aanvraag-schets-input"
                className="hidden"
                onChange={(e) => setSchets(e.target.files?.[0] || null)}
              />
              {schets ? (
                <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#231C17] border border-[#E6AF68] text-sm">
                  <span className="inline-flex items-center gap-2 text-white truncate">
                    <Paperclip size={14} className="text-[#E6AF68] shrink-0" />
                    <span data-testid="aanvraag-schets-naam" className="truncate">{schets.name}</span>
                  </span>
                  <button
                    type="button"
                    data-testid="aanvraag-schets-verwijder"
                    onClick={() => { setSchets(null); if (schetsRef.current) schetsRef.current.value = ""; }}
                    className="text-[#A89C8F] hover:text-white transition-colors"
                    aria-label="Schets verwijderen"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="aanvraag-schets-kiezen"
                  onClick={() => schetsRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#231C17] border border-dashed border-[#4F4137] text-[#A89C8F] text-sm hover:border-[#E6AF68] hover:text-white transition-colors"
                >
                  <Paperclip size={14} />
                  Kies een afbeelding (jpg, png — max 10 MB)
                </button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <span className="text-[11px] text-[#A89C8F]">
                We gaan vertrouwelijk met je gegevens om.
              </span>
              <button
                type="submit"
                data-testid="aanvraag-submit-button"
                disabled={versturen}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#E6AF68] text-[#1C1612] font-bold text-sm hover:bg-[#F0C287] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {versturen && <Loader2 size={16} className="animate-spin" />}
                {versturen ? "Versturen..." : "Verstuur Aanvraag"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
